import { memo, Suspense, useState } from 'react'

import axios from 'axios'
import { type UseInterceptorsInterface, useInterceptors } from 'commons/customHooks/useInterceptors'
import { useNavPrompt } from 'commons/customHooks/useNavPrompt'
import { asyncLocalStorage, CircularProgress, Modal } from 'components'
import { Outlet } from 'react-router-dom'
import api from 'services/api'
import { handleLogout } from 'store/AuthContext/authSlice'
import { useDispatch, useSelector } from 'store/store'

function Main() {
  const {
    serverErrorModal,
    setServerErrorModal,
    timeoutTime,
    modalTitleErrorMessage,
    setModalTitleErrorMessage,
    modalMessage,
    setModalMessage,
  }: UseInterceptorsInterface = useInterceptors()

  const dispatch = useDispatch()
  const { isDirty, confirmedNavigation } = useSelector(({ structure }) => structure)
  const [allowModal, setAllowModal] = useState<boolean>(true)

  // NOTE: Setup navigation blocker for the entire app
  const { showPrompt, confirmNavigation, cancelNavigation } = useNavPrompt(
    isDirty,
    confirmedNavigation,
  )

  api.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const localStorageError = localStorage.getItem('serverErrorModal')
      setAllowModal(true)

      //request canceled by system
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      if (
        // email related to others profiles
        error.response.data.return_code === 6012 ||
        // email or password incorrect
        error.response.data.return_code === 6010 ||
        // code expired
        error.response.data.return_code === 6011 ||
        // new password is the same as the old one
        error.response.data.return_code === 512 ||
        error.response.config?.url?.includes('upload-planilha')
      ) {
        // Don`t handle with modal error in this case
        return Promise.reject(error)
      }

      // NOTE: Handle response error when response data is an error array
      if (error.response && error.response.data instanceof Array) {
        return Promise.reject(error)
      }

      if (!serverErrorModal && !localStorageError && allowModal) {
        if (error.response?.status === 401) {
          asyncLocalStorage.removeItem('token').then(() => {
            dispatch(handleLogout())
          })

          setAllowModal(false)
          localStorage.setItem(
            'serverErrorModalMessage',
            JSON.stringify({
              message: 'Por favor, faça seu login novamente.',
              title: 'Login expirado.',
            }),
          )
        } else if (error.response) {
          const message =
            error.response.data.return_chav ??
            error.response.data.message ??
            error.response.data?.MensagemRetorno ??
            error.response.data?.errors[0]?.message
          localStorage.setItem('serverErrorModalMessage', JSON.stringify({ message }))
        }
        localStorage.setItem('serverErrorModal', JSON.stringify({ open: true }))
        setTimeout(() => {
          localStorage.removeItem('serverErrorModal')
        }, timeoutTime)
      }
      if (localStorageError && allowModal) {
        localStorage.setItem('serverErrorModal', JSON.stringify({ open: true }))
      }
      return Promise.reject(error)
    },
  )

  const handleErrorModalClose = () => {
    setServerErrorModal(false)
    localStorage.removeItem('serverErrorModal')
    localStorage.removeItem('serverErrorModalMessage')
    setModalTitleErrorMessage('...')
    setModalMessage('...')
    setAllowModal(true)
  }

  return (
    <>
      <Suspense fallback={<CircularProgress isRoute />}>
        <Outlet />
        {allowModal && (
          <Modal
            buttonAction={handleErrorModalClose}
            closeAction={handleErrorModalClose}
            description={modalMessage || ''}
            open={serverErrorModal || false}
            setOpen={setServerErrorModal}
            title={modalTitleErrorMessage || ''}
          />
        )}
        <Modal
          buttonAction={cancelNavigation}
          buttonText="CONTINUAR A EDIÇÃO"
          description="Existem campos que não foram salvos, deseja prosseguir mesmo assim?"
          open={showPrompt}
          returnAction={confirmNavigation}
          returnIcon={<></>}
          returnText="DESCARTAR O PROGRESSO"
          setOpen={() => cancelNavigation()}
          showReturn
          title="Continuar de onde parou"
        />
      </Suspense>
    </>
  )
}

export default memo(Main)
